import {
  useJwtUser,
  useStopImpersonation,
  useStartImpersonation,
} from "@granular/fabric3-core";
import { GKButton, GKLoading, GKLoadingType, useAlerts } from "@granular/gds";
import { useSearchParams } from "react-router-dom";
import { useEffect, useRef } from "react";

const IMPERSONATION_QUERY_PARAM = "impersonate";

export const ImpersonationContext = () => {
  const jwt = useJwtUser();
  const alert = useAlerts();
  const startImpersonationHook = useStartImpersonation();
  const stopImpersonationHook = useStopImpersonation();
  const impersonationEmail = jwt?.["custom:granular_impersonation_email"];
  const impersonationId = jwt?.["custom:granular_impersonation_id"];
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const impersonationParam = searchParams.get(IMPERSONATION_QUERY_PARAM);
    if (!impersonationParam) {
      return;
    }
    if (startImpersonationHook.isPending) {
      // Impersonation already in progress
      return;
    }

    // Consume the search param so it doesn't try to impersonate again on reloads
    const newParams = new URLSearchParams(searchParams);
    newParams.delete(IMPERSONATION_QUERY_PARAM);
    setSearchParams(newParams);

    if (
      impersonationEmail === impersonationParam ||
      impersonationId === impersonationParam
    ) {
      // Already impersonating the requested user, don't need to do anything
      return;
    }

    console.log(`Starting impersonation for ${impersonationParam}`);
    startImpersonationHook.mutate(impersonationParam);
    alert.global.info(
      <div className="d-flex">
        <span>Starting impersonation for {impersonationParam}</span>
        <GKLoading
          className="mx-2"
          isLoading={true}
          type={GKLoadingType.Spinner}
          inline={true}
          spinnerProps={{ width: 16, inverse: true }}
        />
      </div>,
      {
        timeout: 5000,
        dismissible: true,
      },
    );
  }, [
    searchParams,
    setSearchParams,
    impersonationEmail,
    impersonationId,
    alert.global,
    startImpersonationHook,
  ]);

  // Show impersonation alert once when claims first become available
  const hasShownAlert = useRef(false);
  useEffect(() => {
    if (hasShownAlert.current || !jwt) {
      return;
    }

    const impersonatedGranularId =
      jwt["custom:granular_impersonation_email"] ??
      jwt["custom:granular_impersonation_id"] ??
      "";

    if (impersonatedGranularId) {
      hasShownAlert.current = true;
      alert.global.info(
        <div className="d-flex">
          <span>You are impersonating user: {impersonatedGranularId}</span>
          <GKButton
            className="ml-auto"
            onClick={() => stopImpersonationHook.mutate()}
            disabled={stopImpersonationHook.isPending}
          >
            <GKLoading
              className="mr-2"
              isLoading={stopImpersonationHook.isPending}
              type={GKLoadingType.Spinner}
              inline={true}
              spinnerProps={{ width: 16, inverse: true }}
            />
            Stop impersonating
          </GKButton>
        </div>,
        {
          dismissible: true,
        },
      );
    }
  }, [jwt, alert.global, stopImpersonationHook]);

  return <></>;
};
